import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  Typography,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Checkbox,
  Radio,
  theme,
  Tooltip,
  Divider,
  Tag
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faTimes,
  faStethoscope,
  faHeart,
  faLungs,
  faUser,
  faHandPaper,
  faFileAlt,
  faClipboardCheck,
  faEye,
  faCircle,
  faBrain,
  faBone,
  faFileContract
} from '@fortawesome/free-solid-svg-icons'

import { allDataPoints } from '../data'

const { Title, Text } = Typography
const { Option } = Select

// Category icons mapping
const categoryIcons = {
  General: faFileAlt,
  Cardiac: faHeart,
  'General Appearance': faUser,
  Skin: faHandPaper,
  Respiratory: faLungs,
  Observations: faClipboardCheck,
  HEENT: faEye,
  Abdomen: faCircle,
  Neurological: faBrain,
  Orthopaedic: faBone,
  Diagnosis: faFileContract
}

const TemplateForm = ({ template, onComplete, onRemove, onDraftSave, existingRecord }) => {
  const { token } = theme.useToken()
  const [formValues, setFormValues] = useState(template.values || {})
  const [isCompleted, setIsCompleted] = useState(existingRecord ? existingRecord.status === 'completed' : false)
  const [completedAt, setCompletedAt] = useState(
    existingRecord && existingRecord.completedAt ? existingRecord.completedAt : null
  )

  // Update state when existingRecord changes (template switching)
  useEffect(() => {
    if (existingRecord) {
      setIsCompleted(existingRecord.status === 'completed')
      setCompletedAt(existingRecord.completedAt || null)
      setFormValues(existingRecord.values || template.values || {})
    } else {
      setIsCompleted(false)
      setCompletedAt(null)
      setFormValues(template.values || {})
    }
  }, [existingRecord, template.values])

  const handleValueChange = (fieldId, value) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldId]: value
    }))
    // If form was completed and user makes changes, mark as not completed
    if (isCompleted) {
      setIsCompleted(false)
      setCompletedAt(null)
    }
  }

  const generateFinalContent = () => {
    let htmlContent = `<p><strong>${template.name}:</strong></p><br>`

    // Handle new data structure where selectedDataPoints is an array of data point objects
    if (template.selectedDataPoints && Array.isArray(template.selectedDataPoints)) {
      // Group selected data points by category
      const pointsByCategory = {}
      template.selectedDataPoints.forEach((point) => {
        if (!pointsByCategory[point.category]) {
          pointsByCategory[point.category] = []
        }
        pointsByCategory[point.category].push(point)
      })

      Object.entries(pointsByCategory).forEach(([category, points]) => {
        if (points.length > 0) {
          htmlContent += `<p><strong>${category} Examination:</strong></p>`

          points.forEach((point) => {
            const value = formValues[point.id]
            if (value !== undefined && value !== null && value !== '') {
              htmlContent += `<p style="margin-left: 15px;"><strong>${point.label}:</strong> `

              let formattedValue = ''
              if (Array.isArray(value)) {
                formattedValue = value.join(', ')
              } else if (typeof value === 'boolean') {
                formattedValue = value ? 'Yes' : 'No'
              } else {
                formattedValue = value
              }

              if (point.units) {
                formattedValue += ` ${point.units}`
              }

              htmlContent += formattedValue + '</p>'

              // Handle subfields for panel type
              if (point.fieldType === 'panel' && point.subFields) {
                point.subFields.forEach((subField) => {
                  if (!subField) return

                  const subValue = formValues[`${point.id}.${subField.id}`]
                  if (subValue !== undefined && subValue !== null && subValue !== '') {
                    htmlContent += `<p style="margin-left: 30px;">• <strong>${subField.label}:</strong> `

                    let formattedSubValue = ''
                    if (Array.isArray(subValue)) {
                      formattedSubValue = subValue.join(', ')
                    } else if (typeof subValue === 'boolean') {
                      formattedSubValue = subValue ? 'Yes' : 'No'
                    } else {
                      formattedSubValue = subValue
                    }

                    if (subField.units) {
                      formattedSubValue += ` ${subField.units}`
                    }

                    htmlContent += formattedSubValue + '</p>'
                  }
                })
              }
            }
          })

          htmlContent += '<br>'
        }
      })
    } else if (template.categories && template.selectedDataPoints) {
      // Handle old data structure for backward compatibility
      template.categories.forEach((category) => {
        const categoryDataPoints = allDataPoints[category] || []
        const selectedInCategory = template.selectedDataPoints[category] || []
        const selectedPoints = categoryDataPoints.filter((dp) => selectedInCategory.includes(dp.id))

        if (selectedPoints.length > 0) {
          htmlContent += `<p><strong>${category} Examination:</strong></p>`

          selectedPoints.forEach((point) => {
            const value = formValues[point.id]
            if (value !== undefined && value !== null && value !== '') {
              htmlContent += `<p style="margin-left: 15px;"><strong>${point.label}:</strong> `

              let formattedValue = ''
              if (Array.isArray(value)) {
                formattedValue = value.join(', ')
              } else if (typeof value === 'boolean') {
                formattedValue = value ? 'Yes' : 'No'
              } else {
                formattedValue = value
              }

              if (point.units) {
                formattedValue += ` ${point.units}`
              }

              htmlContent += formattedValue + '</p>'

              // Handle subfields for panel type
              if (point.fieldType === 'panel' && point.subFields) {
                point.subFields.forEach((subField) => {
                  if (!subField) return

                  const subValue = formValues[`${point.id}.${subField.id}`]
                  if (subValue !== undefined && subValue !== null && subValue !== '') {
                    htmlContent += `<p style="margin-left: 30px;">• <strong>${subField.label}:</strong> `

                    let formattedSubValue = ''
                    if (Array.isArray(subValue)) {
                      formattedSubValue = subValue.join(', ')
                    } else if (typeof subValue === 'boolean') {
                      formattedSubValue = subValue ? 'Yes' : 'No'
                    } else {
                      formattedSubValue = subValue
                    }

                    if (subField.units) {
                      formattedSubValue += ` ${subField.units}`
                    }

                    htmlContent += formattedSubValue + '</p>'
                  }
                })
              }
            }
          })

          htmlContent += '<br>'
        }
      })
    } else {
      // Fallback for old single-category templates
      Object.entries(formValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())

          htmlContent += `<p><strong>${label}:</strong> `

          if (Array.isArray(value)) {
            htmlContent += value.join(', ')
          } else if (typeof value === 'boolean') {
            htmlContent += value ? 'Yes' : 'No'
          } else {
            htmlContent += value
          }

          htmlContent += '</p>'
        }
      })
    }

    return htmlContent
  }

  const handleComplete = () => {
    const finalContent = generateFinalContent()
    onComplete({
      name: template.name,
      categories: template.categories || [template.category],
      content: finalContent,
      values: formValues,
      completedAt: new Date().toISOString()
    })
    setIsCompleted(true)
    setCompletedAt(new Date().toISOString())
  }

  const renderCompletedValue = (dataPoint) => {
    const value = formValues[dataPoint.id]

    if (!value || (Array.isArray(value) && value.length === 0)) {
      return (
        <Text type='secondary' style={{ fontSize: '12px', fontStyle: 'italic' }}>
          Not specified
        </Text>
      )
    }

    // Handle different field types for display
    if (dataPoint.fieldType === 'checkbox' || (Array.isArray(value) && dataPoint.options)) {
      // Show selected options with checkmarks
      return (
        <Space direction='vertical' size='small' style={{ width: '100%' }}>
          {dataPoint.options?.map((option) => {
            const isSelected = Array.isArray(value) ? value.includes(option) : value === option
            return (
              <div
                key={option}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '12px',
                  opacity: isSelected ? 1 : 0.4
                }}
              >
                <FontAwesomeIcon
                  icon={faCheck}
                  style={{
                    color: isSelected ? token.colorSuccess : token.colorTextDisabled,
                    marginRight: '8px',
                    fontSize: '10px'
                  }}
                />
                <Text
                  style={{
                    color: isSelected ? token.colorText : token.colorTextSecondary,
                    fontSize: '12px',
                    textDecoration: isSelected ? 'none' : 'line-through'
                  }}
                >
                  {option}
                </Text>
              </div>
            )
          })}
        </Space>
      )
    } else {
      // Show single values with icon
      let displayValue = value
      if (typeof value === 'boolean') {
        displayValue = value ? 'Yes' : 'No'
      } else if (Array.isArray(value)) {
        displayValue = value.join(', ')
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FontAwesomeIcon
            icon={faCheck}
            style={{
              color: token.colorSuccess,
              marginRight: '8px',
              fontSize: '12px'
            }}
          />
          <Text style={{ fontSize: '12px', fontWeight: '500' }}>
            {displayValue}
            {dataPoint.units && ` ${dataPoint.units}`}
          </Text>
        </div>
      )
    }
  }

  const renderFormField = (dataPoint) => {
    const value = formValues[dataPoint.id]
    const inputConfig = dataPoint.inputConfig || {}
    const inputType = inputConfig.inputType || dataPoint.fieldType

    // If completed, show read-only display
    if (isCompleted) {
      return renderCompletedValue(dataPoint)
    }

    // Use configured input type if available, otherwise fall back to fieldType
    switch (inputType) {
      case 'dropdown':
        // Handle both single and multi-select dropdowns
        return (
          <Select
            mode={inputConfig.multiSelect ? 'multiple' : undefined}
            value={value}
            onChange={(val) => handleValueChange(dataPoint.id, val)}
            placeholder={inputConfig.placeholder || dataPoint.placeholder}
            style={{ width: '100%' }}
            size='small'
            disabled={isCompleted}
          >
            {dataPoint.options?.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
            {inputConfig.allowOther && (
              <Option key='other' value='other'>
                Other (specify below)
              </Option>
            )}
          </Select>
        )

      case 'radio':
        return (
          <Radio.Group
            value={value}
            onChange={(e) => handleValueChange(dataPoint.id, e.target.value)}
            disabled={isCompleted}
          >
            <Space direction='vertical' size='small'>
              {dataPoint.options?.map((option) => (
                <Radio key={option} value={option} style={{ fontSize: '12px' }}>
                  {option}
                </Radio>
              ))}
              {inputConfig.allowOther && (
                <Radio key='other' value='other' style={{ fontSize: '12px' }}>
                  Other (specify below)
                </Radio>
              )}
            </Space>
          </Radio.Group>
        )

      case 'checkbox':
        return (
          <Space direction='vertical' size='small' style={{ width: '100%' }}>
            {dataPoint.options?.map((option) => (
              <Checkbox
                key={option}
                checked={(value || []).includes(option)}
                onChange={(e) => {
                  const currentValues = value || []
                  let newValues
                  if (e.target.checked) {
                    newValues = [...currentValues, option]
                  } else {
                    newValues = currentValues.filter((v) => v !== option)
                  }
                  handleValueChange(dataPoint.id, newValues)
                }}
                disabled={isCompleted}
                style={{ fontSize: '12px' }}
              >
                {option}
              </Checkbox>
            ))}
            {inputConfig.allowOther && (
              <Checkbox
                key='other'
                checked={(value || []).includes('other')}
                onChange={(e) => {
                  const currentValues = value || []
                  let newValues
                  if (e.target.checked) {
                    newValues = [...currentValues, 'other']
                  } else {
                    newValues = currentValues.filter((v) => v !== 'other')
                  }
                  handleValueChange(dataPoint.id, newValues)
                }}
                disabled={isCompleted}
                style={{ fontSize: '12px' }}
              >
                Other (specify below)
              </Checkbox>
            )}
          </Space>
        )

      case 'text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleValueChange(dataPoint.id, e.target.value)}
            placeholder={inputConfig.placeholder || dataPoint.placeholder}
            size='small'
            disabled={isCompleted}
          />
        )

      case 'numeric':
        return (
          <InputNumber
            value={value}
            onChange={(val) => handleValueChange(dataPoint.id, val)}
            placeholder={inputConfig.placeholder || dataPoint.placeholder}
            style={{ width: '100%' }}
            size='small'
            min={dataPoint.validation?.min}
            max={dataPoint.validation?.max}
            precision={dataPoint.validation?.decimals}
            addonAfter={dataPoint.units}
            disabled={isCompleted}
          />
        )
    }

    // Fallback to original fieldType logic
    switch (dataPoint.fieldType) {
      case 'single':
        return (
          <Select
            value={value}
            onChange={(val) => handleValueChange(dataPoint.id, val)}
            placeholder={dataPoint.placeholder}
            style={{ width: '100%' }}
            size='small'
            disabled={isCompleted}
          >
            {dataPoint.options?.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        )

      case 'multi':
        // Check if UI variant is checkbox to render individual checkboxes
        if (dataPoint.ui?.variant === 'checkbox') {
          return (
            <Space direction='vertical' size='small' style={{ width: '100%' }}>
              {dataPoint.options?.map((option) => (
                <Checkbox
                  key={option}
                  checked={(value || []).includes(option)}
                  onChange={(e) => {
                    const currentValues = value || []
                    let newValues
                    if (e.target.checked) {
                      newValues = [...currentValues, option]
                    } else {
                      newValues = currentValues.filter((v) => v !== option)
                    }
                    handleValueChange(dataPoint.id, newValues)
                  }}
                  disabled={isCompleted}
                  style={{ fontSize: '12px' }}
                >
                  {option}
                </Checkbox>
              ))}
            </Space>
          )
        }

        // Default multi-select dropdown
        return (
          <Select
            mode='multiple'
            value={value || []}
            onChange={(val) => handleValueChange(dataPoint.id, val)}
            placeholder={dataPoint.placeholder}
            style={{ width: '100%' }}
            size='small'
            disabled={isCompleted}
          >
            {dataPoint.options?.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        )

      case 'checkbox':
        return (
          <Checkbox
            checked={value || false}
            onChange={(e) => handleValueChange(dataPoint.id, e.target.checked)}
            disabled={isCompleted}
          >
            {dataPoint.label}
          </Checkbox>
        )

      case 'numeric':
        return (
          <InputNumber
            value={value}
            onChange={(val) => handleValueChange(dataPoint.id, val)}
            placeholder={dataPoint.placeholder}
            style={{ width: '100%' }}
            size='small'
            min={dataPoint.validation?.min}
            max={dataPoint.validation?.max}
            precision={dataPoint.validation?.decimals}
            addonAfter={dataPoint.units}
            disabled={isCompleted}
          />
        )

      case 'text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleValueChange(dataPoint.id, e.target.value)}
            placeholder={dataPoint.placeholder}
            size='small'
            disabled={isCompleted}
          />
        )

      case 'date':
        return (
          <DatePicker
            value={value}
            onChange={(date) => handleValueChange(dataPoint.id, date)}
            style={{ width: '100%' }}
            size='small'
            disabled={isCompleted}
          />
        )

      case 'panel':
        return (
          <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px' }}>
            <Text strong style={{ fontSize: '12px', color: token.colorTextSecondary }}>
              {dataPoint.label} Details
            </Text>
            <Row gutter={[8, 8]} style={{ marginTop: '8px' }}>
              {dataPoint.subFields?.map((subField) => (
                <Col key={subField.id} span={subField.ui?.span || 12}>
                  <div>
                    <Text style={{ fontSize: '11px', color: token.colorTextSecondary }}>{subField.label}</Text>
                    {renderSubFieldInput(dataPoint.id, subField)}
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )

      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleValueChange(dataPoint.id, e.target.value)}
            placeholder={dataPoint.placeholder}
            size='small'
            disabled={isCompleted}
          />
        )
    }
  }

  const renderCompletedSubValue = (parentId, subField) => {
    const fieldKey = `${parentId}.${subField.id}`
    const value = formValues[fieldKey]

    if (!value || (Array.isArray(value) && value.length === 0)) {
      return (
        <Text type='secondary' style={{ fontSize: '11px', fontStyle: 'italic' }}>
          Not specified
        </Text>
      )
    }

    if (Array.isArray(value) && subField.options) {
      // Show selected options with checkmarks for multi-select
      return (
        <Space direction='vertical' size='small' style={{ width: '100%' }}>
          {subField.options?.map((option) => {
            const isSelected = value.includes(option)
            return (
              <div
                key={option}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '11px',
                  opacity: isSelected ? 1 : 0.4
                }}
              >
                <FontAwesomeIcon
                  icon={faCheck}
                  style={{
                    color: isSelected ? token.colorSuccess : token.colorTextDisabled,
                    marginRight: '6px',
                    fontSize: '9px'
                  }}
                />
                <Text
                  style={{
                    color: isSelected ? token.colorText : token.colorTextSecondary,
                    fontSize: '11px',
                    textDecoration: isSelected ? 'none' : 'line-through'
                  }}
                >
                  {option}
                </Text>
              </div>
            )
          })}
        </Space>
      )
    } else {
      // Show single values
      let displayValue = value
      if (typeof value === 'boolean') {
        displayValue = value ? 'Yes' : 'No'
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FontAwesomeIcon
            icon={faCheck}
            style={{
              color: token.colorSuccess,
              marginRight: '6px',
              fontSize: '10px'
            }}
          />
          <Text style={{ fontSize: '11px', fontWeight: '500' }}>
            {displayValue}
            {subField.units && ` ${subField.units}`}
          </Text>
        </div>
      )
    }
  }

  const renderSubFieldInput = (parentId, subField) => {
    const fieldKey = `${parentId}.${subField.id}`
    const value = formValues[fieldKey]

    // If completed, show read-only display
    if (isCompleted) {
      return renderCompletedSubValue(parentId, subField)
    }

    switch (subField.fieldType) {
      case 'single':
        return (
          <Select
            value={value}
            onChange={(val) => handleValueChange(fieldKey, val)}
            style={{ width: '100%' }}
            size='small'
            disabled={isCompleted}
          >
            {subField.options?.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        )

      case 'multi':
        // Check if UI variant is checkbox to render individual checkboxes
        if (subField.ui?.variant === 'checkbox') {
          return (
            <Space direction='vertical' size='small' style={{ width: '100%' }}>
              {subField.options?.map((option) => (
                <Checkbox
                  key={option}
                  checked={(value || []).includes(option)}
                  onChange={(e) => {
                    const currentValues = value || []
                    let newValues
                    if (e.target.checked) {
                      newValues = [...currentValues, option]
                    } else {
                      newValues = currentValues.filter((v) => v !== option)
                    }
                    handleValueChange(fieldKey, newValues)
                  }}
                  disabled={isCompleted}
                  style={{ fontSize: '12px' }}
                >
                  {option}
                </Checkbox>
              ))}
            </Space>
          )
        }

        // Default multi-select dropdown
        return (
          <Select
            mode='multiple'
            value={value || []}
            onChange={(val) => handleValueChange(fieldKey, val)}
            style={{ width: '100%' }}
            size='small'
            disabled={isCompleted}
          >
            {subField.options?.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        )

      case 'numeric':
        return (
          <InputNumber
            value={value}
            onChange={(val) => handleValueChange(fieldKey, val)}
            style={{ width: '100%' }}
            size='small'
            min={subField.validation?.min}
            max={subField.validation?.max}
            precision={subField.validation?.decimals}
            addonAfter={subField.units}
            disabled={isCompleted}
          />
        )

      case 'checkbox':
        return (
          <Checkbox
            checked={value || false}
            onChange={(e) => handleValueChange(fieldKey, e.target.checked)}
            disabled={isCompleted}
          />
        )

      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleValueChange(fieldKey, e.target.value)}
            size='small'
            disabled={isCompleted}
          />
        )
    }
  }

  // Get all selected data points across categories
  const getAllSelectedDataPoints = () => {
    // Handle new data structure where selectedDataPoints is already an array of data point objects
    if (template.selectedDataPoints && Array.isArray(template.selectedDataPoints)) {
      return template.selectedDataPoints
    }

    // Handle old data structure for backward compatibility
    const allPoints = []
    if (template.categories && template.selectedDataPoints) {
      template.categories.forEach((category) => {
        const categoryDataPoints = allDataPoints[category] || []
        const selectedInCategory = template.selectedDataPoints[category] || []
        const selectedPoints = categoryDataPoints.filter((dp) => selectedInCategory.includes(dp.id))
        allPoints.push(...selectedPoints.map((point) => ({ ...point, category })))
      })
    }

    return allPoints
  }

  const selectedDataPoints = getAllSelectedDataPoints()
  const categories = template.categories || [template.category]

  return (
    <Card
      size='small'
      style={{
        marginBottom: '16px',
        borderRadius: '8px',
        boxShadow: isCompleted ? '0 2px 8px rgba(82, 196, 26, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
        borderLeft: `4px solid ${isCompleted ? token.colorSuccess : token.colorPrimary}`,
        backgroundColor: isCompleted ? 'rgba(246, 255, 237, 0.5)' : 'white'
      }}
      title={
        <Space>
          <FontAwesomeIcon
            icon={isCompleted ? faCheck : faStethoscope}
            style={{
              color: isCompleted ? token.colorSuccess : token.colorPrimary,
              fontSize: '14px'
            }}
          />
          <Text strong style={{ fontSize: '14px' }}>
            {template.name}
            {isCompleted && (
              <Text type='success' style={{ fontSize: '12px', marginLeft: '8px' }}>
                (Completed)
              </Text>
            )}
          </Text>
          <Space>
            {categories.map((category) => {
              const icon = categoryIcons[category] || faStethoscope
              return (
                <Tag key={category} size='small' style={{ fontSize: '10px' }}>
                  <FontAwesomeIcon icon={icon} style={{ marginRight: '4px', fontSize: '10px' }} />
                  {category}
                </Tag>
              )
            })}
          </Space>
          {isCompleted && completedAt && (
            <Text type='secondary' style={{ fontSize: '11px' }}>
              Completed: {new Date(completedAt).toLocaleTimeString()}
            </Text>
          )}
        </Space>
      }
      extra={
        <Space>
          {isCompleted ? (
            <>
              <Tooltip title='Edit template values'>
                <Button
                  type='default'
                  size='small'
                  icon={<FontAwesomeIcon icon={faStethoscope} />}
                  onClick={() => {
                    setIsCompleted(false)
                    setCompletedAt(null)
                  }}
                >
                  Edit
                </Button>
              </Tooltip>
              <Tooltip title='Re-insert updated values into clinical notes'>
                <Button type='primary' size='small' icon={<FontAwesomeIcon icon={faCheck} />} onClick={handleComplete}>
                  Update Notes
                </Button>
              </Tooltip>
            </>
          ) : (
            <Tooltip title='Complete and insert into clinical notes'>
              <Button type='primary' size='small' icon={<FontAwesomeIcon icon={faCheck} />} onClick={handleComplete}>
                Complete
              </Button>
            </Tooltip>
          )}
          <Tooltip title='Remove this template'>
            <Button type='text' size='small' danger icon={<FontAwesomeIcon icon={faTimes} />} onClick={onRemove} />
          </Tooltip>
        </Space>
      }
    >
      {selectedDataPoints.length === 0 ? (
        <Text type='secondary' style={{ fontSize: '12px' }}>
          No fields configured for this template
        </Text>
      ) : categories.length > 1 ? (
        // Multi-category template - use cards
        <Space direction='vertical' style={{ width: '100%' }}>
          {categories.map((category) => {
            // Get data points for this category from the selectedDataPoints array
            const categoryPoints = selectedDataPoints.filter((point) => point.category === category)
            const icon = categoryIcons[category] || faStethoscope

            if (categoryPoints.length === 0) return null

            return (
              <Card
                key={category}
                size='small'
                style={{
                  borderRadius: '8px',
                  boxShadow: isCompleted ? '0 1px 4px rgba(82, 196, 26, 0.1)' : '0 1px 4px rgba(0, 0, 0, 0.1)',
                  borderLeft: `4px solid ${isCompleted ? token.colorSuccess : token.colorPrimary}`,
                  marginBottom: '12px',
                  backgroundColor: isCompleted ? 'rgba(246, 255, 237, 0.3)' : 'white'
                }}
                styles={{
                  header: {
                    background: token.colorPrimaryBg,
                    borderBottom: `1px solid ${token.colorBorder}`,
                    padding: '8px 16px'
                  },
                  body: {
                    padding: '16px'
                  }
                }}
                title={
                  <Space>
                    <FontAwesomeIcon
                      icon={icon}
                      style={{
                        color: isCompleted ? token.colorSuccess : token.colorPrimary,
                        fontSize: '14px'
                      }}
                    />
                    <Text
                      strong
                      style={{
                        color: isCompleted ? token.colorSuccess : token.colorPrimary,
                        fontSize: '14px'
                      }}
                    >
                      {category} Examination
                    </Text>
                    <Tag
                      size='small'
                      style={{
                        fontSize: '10px',
                        background: isCompleted ? 'rgba(246, 255, 237, 0.8)' : token.colorPrimaryBg,
                        border: `1px solid ${isCompleted ? token.colorSuccess : token.colorPrimary}`,
                        color: isCompleted ? token.colorSuccess : token.colorPrimary
                      }}
                    >
                      {categoryPoints.length} field{categoryPoints.length > 1 ? 's' : ''}
                    </Tag>
                  </Space>
                }
              >
                <Row gutter={[16, 12]}>
                  {categoryPoints.map((point) => (
                    <Col key={point.id} span={8}>
                      <div style={{ marginBottom: '8px' }}>
                        <Text
                          strong
                          style={{
                            fontSize: '12px',
                            display: 'block',
                            marginBottom: '6px',
                            color: token.colorText
                          }}
                        >
                          {point.label}
                          {point.inputConfig?.required && (
                            <span style={{ color: token.colorError, marginLeft: '2px' }}>*</span>
                          )}
                          {point.units && (
                            <Tag
                              size='small'
                              style={{
                                marginLeft: '6px',
                                fontSize: '10px',
                                background: token.colorFillSecondary,
                                border: 'none',
                                color: token.colorTextSecondary
                              }}
                            >
                              {point.units}
                            </Tag>
                          )}
                        </Text>
                        {renderFormField(point)}
                        {point.inputConfig?.helpText && (
                          <Text
                            type='secondary'
                            style={{
                              fontSize: '11px',
                              display: 'block',
                              marginTop: '4px',
                              fontStyle: 'italic'
                            }}
                          >
                            {point.inputConfig.helpText}
                          </Text>
                        )}
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card>
            )
          })}
        </Space>
      ) : (
        // Single category template - simple grid with card wrapper
        <Card
          size='small'
          style={{
            borderRadius: '8px',
            boxShadow: isCompleted ? '0 1px 4px rgba(82, 196, 26, 0.1)' : '0 1px 4px rgba(0, 0, 0, 0.1)',
            borderLeft: `4px solid ${isCompleted ? token.colorSuccess : token.colorPrimary}`,
            backgroundColor: isCompleted ? 'rgba(246, 255, 237, 0.3)' : 'white'
          }}
          styles={{
            header: {
              background: token.colorPrimaryBg,
              borderBottom: `1px solid ${token.colorBorder}`,
              padding: '8px 16px'
            },
            body: {
              padding: '16px'
            }
          }}
          title={
            selectedDataPoints.length > 0 && (
              <Space>
                <FontAwesomeIcon
                  icon={categoryIcons[selectedDataPoints[0].category] || faStethoscope}
                  style={{
                    color: isCompleted ? token.colorSuccess : token.colorPrimary,
                    fontSize: '14px'
                  }}
                />
                <Text
                  strong
                  style={{
                    color: isCompleted ? token.colorSuccess : token.colorPrimary,
                    fontSize: '14px'
                  }}
                >
                  {selectedDataPoints[0].category} Examination
                </Text>
                <Tag
                  size='small'
                  style={{
                    fontSize: '10px',
                    background: isCompleted ? 'rgba(246, 255, 237, 0.8)' : token.colorPrimaryBg,
                    border: `1px solid ${isCompleted ? token.colorSuccess : token.colorPrimary}`,
                    color: isCompleted ? token.colorSuccess : token.colorPrimary
                  }}
                >
                  {selectedDataPoints.length} field{selectedDataPoints.length > 1 ? 's' : ''}
                </Tag>
              </Space>
            )
          }
        >
          <Row gutter={[16, 12]}>
            {selectedDataPoints.map((point) => (
              <Col key={point.id} span={8}>
                <div style={{ marginBottom: '8px' }}>
                  <Text
                    strong
                    style={{
                      fontSize: '12px',
                      display: 'block',
                      marginBottom: '6px',
                      color: token.colorText
                    }}
                  >
                    {point.label}
                    {point.inputConfig?.required && (
                      <span style={{ color: token.colorError, marginLeft: '2px' }}>*</span>
                    )}
                    {point.units && (
                      <Tag
                        size='small'
                        style={{
                          marginLeft: '6px',
                          fontSize: '10px',
                          background: token.colorFillSecondary,
                          border: 'none',
                          color: token.colorTextSecondary
                        }}
                      >
                        {point.units}
                      </Tag>
                    )}
                  </Text>
                  {renderFormField(point)}
                  {point.inputConfig?.helpText && (
                    <Text
                      type='secondary'
                      style={{
                        fontSize: '11px',
                        display: 'block',
                        marginTop: '4px',
                        fontStyle: 'italic'
                      }}
                    >
                      {point.inputConfig.helpText}
                    </Text>
                  )}
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      )}
    </Card>
  )
}

TemplateForm.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    category: PropTypes.string, // For backward compatibility
    categories: PropTypes.array, // New multi-category support
    selectedDataPoints: PropTypes.oneOfType([PropTypes.array, PropTypes.object]), // New structure (array) or old structure (object)
    dataPoints: PropTypes.array, // For backward compatibility
    values: PropTypes.object
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onDraftSave: PropTypes.func,
  existingRecord: PropTypes.shape({
    _id: PropTypes.string,
    status: PropTypes.string,
    completedAt: PropTypes.string,
    values: PropTypes.object
  })
}

export default TemplateForm
